import React, { useEffect, useMemo, useState, Component } from 'react';
import { FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircleIcon, ImageIcon, MessageCircleIcon, MessageSquareTextIcon, SmartphoneIcon } from 'lucide-react';
import { toast } from 'sonner';
import { BookingCalendar } from '../components/BookingCalendar';
import { sendBookingEmail } from '../utils/notifications';
import { createBooking, getSupabaseErrorMessage, loadAvailability, loadProducts, loadPublicSettings } from '../data/repository';
import { AvailabilityData, Booking as BookingType, Product } from '../data/types';
const TIME_OPTIONS = Array.from({
  length: 23
}, (_, index) => {
  const minutes = 9 * 60 + index * 30;
  return `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
});
const emptyAvailability: AvailabilityData = {
  bookings: [],
  blockedDates: []
};
const PICKUP_ADDRESS = 'Poljanski Lug 20, 10340 Vrbovec';
export function Booking() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const preselectedProductId = params.get('product') || '';
  const [products, setProducts] = useState<Product[]>([]);
  const [availability, setAvailability] = useState<AvailabilityData>(emptyAvailability);
  const [isLoading, setIsLoading] = useState(true);
  const [availabilityLoading, setAvailabilityLoading] = useState(true);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successBooking, setSuccessBooking] = useState<BookingType | null>(null);
  const [emailNotificationSent, setEmailNotificationSent] = useState<boolean | null>(null);
  const [emailNotificationMessage, setEmailNotificationMessage] = useState('');
  const [adminPhone, setAdminPhone] = useState('');
  const [productId, setProductId] = useState(preselectedProductId);
  const [variant, setVariant] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('20:00');
  const [customerName, setCustomerName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [address, setAddress] = useState('');
  const [includeDelivery, setIncludeDelivery] = useState(true);
  const [note, setNote] = useState('');
  const [locationFile, setLocationFile] = useState<File | null>(null);
  const [locationPreview, setLocationPreview] = useState('');
  const selectedProduct = useMemo(() => products.find(item => item.id === productId), [products, productId]);
  const total = selectedProduct?.variants?.find(item => item.label === variant)?.price || selectedProduct?.basePrice || 0;
  const refreshAvailability = async () => {
    setAvailabilityLoading(true);
    try {
      setAvailability(await loadAvailability());
    } catch (loadError) {
      setError(getSupabaseErrorMessage(loadError));
    } finally {
      setAvailabilityLoading(false);
    }
  };
  useEffect(() => {
    window.scrollTo(0, 0);
    void loadProducts().then(items => {
      const visible = items.filter(item => item.isActive !== false);
      setProducts(visible);
      const initial = visible.find(item => item.id === preselectedProductId);
      if (initial?.variants?.[0]) setVariant(initial.variants[0].label);
    }).catch(loadError => setError(getSupabaseErrorMessage(loadError))).finally(() => setIsLoading(false));
    void refreshAvailability();
  }, [preselectedProductId]);
  useEffect(() => {
    void loadPublicSettings().then(settings => setAdminPhone(settings.contactInfo.phone)).catch(() => setAdminPhone(''));
  }, []);
  useEffect(() => () => {
    if (locationPreview) URL.revokeObjectURL(locationPreview);
  }, [locationPreview]);
  const chooseProduct = (value: string) => {
    setProductId(value);
    const item = products.find(product => product.id === value);
    setVariant(item?.variants?.[0]?.label || '');
    setStartDate('');
    setEndDate('');
  };
  const selectLocationImage = (file?: File) => {
    if (!file) return;
    if (file.size > 50 * 1024 * 1024) {
      toast.error('Slika je veća od 50 MB.');
      return;
    }
    if (locationPreview) URL.revokeObjectURL(locationPreview);
    setLocationFile(file);
    setLocationPreview(URL.createObjectURL(file));
  };
  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selectedProduct || !startDate || !customerName || !phone || !address) {
      toast.error('Molimo ispunite sva obavezna polja.');
      return;
    }
    if (startTime >= endTime) {
      toast.error('Vrijeme završetka mora biti nakon vremena početka.');
      return;
    }
    setIsSubmitting(true);
    try {
      const freshAvailability = await loadAvailability();
      const rangeEnd = endDate || startDate;
      if (!rangeAvailable(productId, startDate, rangeEnd, freshAvailability)) {
        setAvailability(freshAvailability);
        toast.error('Termin je upravo zauzet. Odaberite drugi datum.');
        return;
      }
      const booking = await createBooking({
        id: createId(),
        customerName,
        phone,
        email,
        eventAddress: address,
        productId,
        productName: selectedProduct.name,
        date: startDate,
        endDate: endDate || undefined,
        startTime,
        endTime,
        totalPrice: total,
        includeDelivery,
        note: note || undefined,
        status: 'pending',
        createdAt: new Date().toISOString(),
        selectedVariant: variant || undefined
      }, locationFile);
      let notificationSent = false;
      let notificationMessage = '';
      try {
        const settings = await loadPublicSettings();
        const notification = await sendBookingEmail(booking, settings);
        notificationSent = notification.sent;
        notificationMessage = notification.message;
      } catch (notificationError) {
        console.error('Email obavijest nije poslana:', notificationError);
        notificationMessage = getSupabaseErrorMessage(notificationError);
      }
      setEmailNotificationSent(notificationSent);
      setEmailNotificationMessage(notificationMessage);
      await refreshAvailability();
      window.scrollTo({
        top: 0,
        behavior: 'auto'
      });
      setSuccessBooking(booking);
      toast.success(notificationSent ? 'Zahtjev je poslan, a admin je obaviješten emailom.' : 'Zahtjev je spremljen. Za bržu obradu pošaljite sažetak JumpLandu.');
    } catch (submitError) {
      toast.error(getSupabaseErrorMessage(submitError));
    } finally {
      setIsSubmitting(false);
    }
  };
  if (successBooking) {
    const adminMessagingPhone = normalizeCroatianPhone(adminPhone);
    const bookingMessage = buildBookingMessage(successBooking);
    const openChannel = (channel: 'whatsapp' | 'viber' | 'sms') => {
      if (!adminMessagingPhone) return;
      const url = channel === 'whatsapp' ? `https://wa.me/${adminMessagingPhone}?text=${encodeURIComponent(bookingMessage)}` : channel === 'viber' ? buildViberChatUrl(adminMessagingPhone) : `sms:+${adminMessagingPhone}?body=${encodeURIComponent(bookingMessage)}`;
      toast.success(channel === 'viber' ? 'Otvaramo Viber razgovor s JumpLandom.' : 'Otvaramo pripremljenu poruku za rezervaciju u novoj kartici.');
      window.open(url, '_blank', 'noopener,noreferrer');
    };
    return <div className="min-h-screen bg-background flex items-center justify-center p-4" data-id="element-427" data-mp="3bg77tbg662tq">
        <section className="w-full max-w-lg rounded-3xl border border-cyan-100 bg-white p-6 text-center shadow-sm sm:p-8" data-id="element-428" data-mp="vorq3ekr3rpa">
          <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-3xl bg-green-50 text-green-600" data-id="element-429" data-mp="qq85bhhw8lyi">
            <CheckCircleIcon size={40} data-id="element-430" data-mp="2l4vg82fdifsb" />
          </span>
          <h1 className="mt-5 text-3xl font-display font-bold text-gray-900" data-id="element-431" data-mp="2p9l87g6wva7y">
            Zahtjev za termin je poslan!
          </h1>
          <p className="mt-3 text-gray-600" data-id="element-432" data-mp="2u5jqqn5k5y0g">
            Vaš termin još nije potvrđen. Rezervacija postaje važeća tek nakon
            što od JumpLanda primite poruku potvrde.
          </p>
          {emailNotificationSent === true ? <p className="mt-5 rounded-2xl border border-green-200 bg-green-50 p-4 text-sm font-medium text-green-900" data-id="element-433" data-mp="38w13h2nn8acn">
              Administrativna email obavijest je uspješno poslana.
            </p> : <div className="mt-5 rounded-2xl border border-yellow-200 bg-yellow-50 p-4 text-left text-sm text-yellow-900" data-id="element-434" data-mp="3hw5z6fjh5kaz">
              <p className="font-bold" data-id="element-435" data-mp="1ilo3mc2ntam7">Email obavijest nije potvrđena.</p>
              <p className="mt-1 leading-6" data-id="element-436" data-mp="1nk94mdpqjybp">
                {emailNotificationMessage || 'Za bržu obradu pošaljite sažetak zahtjeva JumpLandu putem jednog od kanala u nastavku.'}
              </p>
            </div>}

          {adminMessagingPhone ? <div className="mt-7 space-y-3 text-left" data-id="element-437" data-mp="169ca11b9netm">
              <p className="rounded-2xl border border-yellow-200 bg-yellow-50 p-4 text-sm font-medium leading-6 text-yellow-900" data-id="element-438" data-mp="167ssl7dvixq9">
                Za bržu obradu odaberite način slanja sažetka zahtjeva
                JumpLandu.
              </p>
              <BookingChannelButton icon={<MessageCircleIcon size={23} data-id="element-440" data-mp="1u0993dhyqhng" />} label="WhatsApp" description="Pošaljite detalje rezervacije izravno na JumpLand broj." className="border-[#25D366]/30 bg-[#25D366]/10 text-[#157f3b] hover:bg-[#25D366]/20 focus:ring-[#25D366]" onClick={() => openChannel('whatsapp')} data-id="element-439" data-mp="1tyy45acs73ur" />
              <BookingChannelButton icon={<MessageSquareTextIcon size={23} data-id="element-442" data-mp="9ibga9kdydfl" />} label="Viber" description="Otvara Viber razgovor izravno s JumpLand brojem, bez poruke." className="border-violet-200 bg-violet-50 text-violet-700 hover:bg-violet-100 focus:ring-violet-500" onClick={() => openChannel('viber')} data-id="element-441" data-mp="9jczx2fgw88x" />
              <BookingChannelButton icon={<SmartphoneIcon size={23} data-id="element-444" data-mp="2gtz7i5a4mrm1" />} label="SMS" description="Otvara SMS prema JumpLand broju s detaljima rezervacije." className="border-cyan-200 bg-cyan-50 text-cyan-700 hover:bg-cyan-100 focus:ring-cyan-500" onClick={() => openChannel('sms')} data-id="element-443" data-mp="2gvtqvf5r2bo0" />
            </div> : <p className="mt-6 rounded-2xl border border-yellow-200 bg-yellow-50 p-4 text-sm text-yellow-900" data-id="element-445" data-mp="2znmjv9ktjwvq">
              Kontakt broj trenutačno nije postavljen. Vaš je zahtjev spremljen,
              a termin će biti potvrđen tek nakon poruke od JumpLanda.
            </p>}

          <button type="button" onClick={() => navigate('/')} className="mt-6 w-full rounded-2xl bg-gray-900 px-5 py-3 font-bold text-white transition-colors hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:ring-offset-2" data-id="element-446" data-mp="3gwx957biy7om">
            Povratak na početnu
          </button>
        </section>
      </div>;
  }
  if (isLoading) return <div className="min-h-screen bg-background py-24 text-center text-gray-500" data-id="element-447" data-mp="2y4moyuongyfu">
        Učitavamo obrazac za rezervaciju…
      </div>;
  return <div className="min-h-screen bg-background py-12" data-id="element-448" data-mp="2p4zk1ywppai1">
      <div className="max-w-6xl mx-auto px-4" data-id="element-449" data-mp="2p6aom0xzema7">
        <header className="mb-12 text-center" data-id="element-450" data-mp="2t61jafqmpmk0">
          <h1 className="text-5xl font-display font-bold text-gray-900" data-id="element-451" data-mp="2t6tgfuzvjsce">
            Rezervacija termina
          </h1>
          <p className="mt-4 text-xl text-gray-600" data-id="element-452" data-mp="nb8l94bsvwg0">
            Ispunite formu i osigurajte zabavu na vrijeme.
          </p>
        </header>
        {error && <p role="alert" className="mb-6 rounded-2xl border border-red-200 bg-red-50 p-4 text-red-800" data-id="element-453" data-mp="20coc9rd1tt27">
            {error}
          </p>}
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12" data-id="element-454" data-mp="1raf438596brz">
          <form onSubmit={handleSubmit} className="space-y-6 rounded-3xl border border-gray-100 bg-white p-6 shadow-sm lg:col-span-7" data-id="element-455" data-mp="1mc3t2u381v86">
            <Field label="Odaberite proizvod *" data-id="element-456" data-mp="2j1l5drwpmfiu">
              <select value={productId} onChange={event => chooseProduct(event.target.value)} required className="input" data-id="element-457" data-mp="2f09frmdu5uan">
                <option value="" data-id="element-458" data-mp="25brihf4rm5br">-- Odaberite --</option>
                {products.map(product => <option key={product.id} value={product.id} data-id="element-459" data-mp="32qltjdvril95">
                    {product.name} ({product.basePrice} €)
                  </option>)}
              </select>
            </Field>
            {selectedProduct?.variants?.length ? <Field label="Opcija *" data-id="element-460" data-mp="35d4pysnrg4h0">
                <select value={variant} onChange={event => setVariant(event.target.value)} className="input" data-id="element-461" data-mp="3a93au69vr4co">
                  {selectedProduct.variants.map(item => <option key={item.label} value={item.label} data-id="element-462" data-mp="3j8gkayy07sxe">
                      {item.label} ({item.price} €)
                    </option>)}
                </select>
              </Field> : null}
            <Field label="Odaberite datum *" data-id="element-463" data-mp="1ubh2t5xg3t6">
              <BookingCalendar productId={productId} selectedStartDate={startDate} selectedEndDate={endDate} availability={availability} isLoading={availabilityLoading} onSelectRange={(start, end) => {
              setStartDate(start);
              setEndDate(end);
            }} data-id="element-464" data-mp="1ul5icbbq2wr" />
            </Field>
            <div className="grid grid-cols-2 gap-4" data-id="element-465" data-mp="24r78q02m0py">
              <Field label="Vrijeme početka *" data-id="element-466" data-mp="3u9v5eodpk77r">
                <select value={startTime} onChange={event => setStartTime(event.target.value)} className="input" data-id="element-467" data-mp="3u93h7ymfxbri">
                  {TIME_OPTIONS.slice(0, -1).map(item => <option key={item} data-id="element-468" data-mp="3feov3p7kxm8m">{item}</option>)}
                </select>
              </Field>
              <Field label="Vrijeme završetka *" data-id="element-469" data-mp="3bdmvv8dnxa6n">
                <select value={endTime} onChange={event => setEndTime(event.target.value)} className="input" data-id="element-470" data-mp="3pbjqw0cgx8j7">
                  {TIME_OPTIONS.filter(item => item > startTime).map(item => <option key={item} data-id="element-471" data-mp="zmrl16smdh1j">{item}</option>)}
                </select>
              </Field>
            </div>
            <hr className="border-gray-100" data-id="element-472" data-mp="2hitakweupbqe" />
            <h2 className="text-lg font-bold text-gray-900" data-id="element-473" data-mp="2miz46i4ol8b4">Vaši podaci</h2>
            <Field label="Ime i prezime *" data-id="element-474" data-mp="2mguv7lrxdj3z">
              <input required value={customerName} onChange={event => setCustomerName(event.target.value)} className="input" data-id="element-475" data-mp="28kho96l0p03r" />
            </Field>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2" data-id="element-476" data-mp="318n69lkosazw">
              <Field label="Broj mobitela *" data-id="element-477" data-mp="359yt5jc6ei4e">
                <input required type="tel" value={phone} onChange={event => setPhone(event.target.value)} className="input" data-id="element-478" data-mp="35bta6mkbtq37" />
              </Field>
              <Field label="Email" data-id="element-479" data-mp="3k5y73alyvchg">
                <input type="email" value={email} onChange={event => setEmail(event.target.value)} className="input" data-id="element-480" data-mp="3k4wjk6nrkxiu" />
              </Field>
            </div>
            <Field label={includeDelivery ? 'Adresa dostave *' : 'Adresa preuzimanja *'} data-id="element-481" data-mp="3tu20wxi76ds0">
              <input required value={address} onChange={event => setAddress(event.target.value)} className="input" data-id="element-482" data-mp="3otnxjsrjjs0b" />
            </Field>
            <div className="grid grid-cols-2 gap-3" data-id="element-483" data-mp="3fwncnw3okc1m">
              <button type="button" onClick={() => {
              setIncludeDelivery(true);
              if (address === PICKUP_ADDRESS) setAddress('');
            }} className={`rounded-2xl border-2 p-4 text-left ${includeDelivery ? 'border-primary bg-primary/10' : 'border-gray-200'}`} data-id="element-484" data-mp="3b3bzzec6t11s">
                <strong data-id="element-485" data-mp="2s5hs53msovkz">S dostavom</strong>
                <span className="mt-1 block text-xs leading-5 text-gray-600" data-id="element-486" data-mp="2s5rnl6qlzuy2">
                  Cijena dostave ovisi o lokaciji dostave.
                </span>
              </button>
              <button type="button" onClick={() => {
              setIncludeDelivery(false);
              setAddress(PICKUP_ADDRESS);
            }} className={`rounded-2xl border-2 p-4 text-left ${!includeDelivery ? 'border-primary bg-primary/10' : 'border-gray-200'}`} data-id="element-487" data-mp="1bnraeqswovb8">
                <strong data-id="element-488" data-mp="nxdg0tmumkdh">Bez dostave</strong>
                <span className="mt-1 block text-xs text-gray-600" data-id="element-489" data-mp="sxj82ax168w3">
                  Samostalno preuzimanje: Poljanski Lug 20, 10340 Vrbovec
                </span>
              </button>
            </div>
            <Field label="Napomena" data-id="element-490" data-mp="12p5t0yz67mc1">
              <textarea value={note} onChange={event => setNote(event.target.value)} rows={3} className="input" data-id="element-491" data-mp="12q7596rxaevc" />
            </Field>
            <div data-id="element-492" data-mp="a5gr0ygh8qlb">
              <p className="mb-2 text-sm font-bold text-gray-700" data-id="element-493" data-mp="2zpljwy24wrzn">
                Slika lokacije (opcionalno)
              </p>
              {locationPreview ? <div className="relative inline-block" data-id="element-494" data-mp="2usv8io19ykkk">
                  <img src={locationPreview} alt="Pregled lokacije" className="h-36 w-36 rounded-2xl object-cover" data-id="element-495" data-mp="38rmh5uxdbe1b" />
                  <button type="button" onClick={() => {
                setLocationFile(null);
                URL.revokeObjectURL(locationPreview);
                setLocationPreview('');
              }} className="absolute -right-2 -top-2 rounded-full bg-red-500 px-2 py-1 text-white" aria-label="Ukloni sliku" data-id="element-496" data-mp="3inxzb1jn2y3o">
                    ×
                  </button>
                </div> : <label className="inline-flex cursor-pointer items-center gap-2 rounded-2xl border-2 border-dashed border-gray-300 px-5 py-3 font-bold text-gray-700" data-id="element-497" data-mp="cb878v5g37h">
                  <ImageIcon size={18} data-id="element-498" data-mp="ovwglhj7p4iy" />
                  Odaberi sliku
                  <input type="file" accept="image/*" className="hidden" onChange={event => {
                selectLocationImage(event.target.files?.[0]);
                event.target.value = '';
              }} data-id="element-499" data-mp="9ydpi8j2af8c" />
                </label>}
              <p className="mt-2 text-xs text-gray-500" data-id="element-500" data-mp="5y3euikbr0n5">
                Slika se privatno sprema uz rezervaciju i vidljiva je samo
                administratoru.
              </p>
            </div>
            <button disabled={isSubmitting || availabilityLoading || !startDate} className="w-full rounded-2xl bg-primary py-5 text-lg font-bold text-white disabled:bg-gray-200 disabled:text-gray-500" data-id="element-501" data-mp="3scmsue5e0dt3">
              {isSubmitting ? 'Šaljemo…' : 'Pošalji rezervaciju'}
            </button>
          </form>
          <aside className="lg:col-span-5" data-id="element-502" data-mp="2dksmkgybexdx">
            <div className="sticky top-24 rounded-3xl border border-gray-100 bg-white p-6 shadow-sm" data-id="element-503" data-mp="2dlkg7m8cql1k">
              <h2 className="text-2xl font-bold text-gray-900" data-id="element-504" data-mp="1zmb00mk5bqxl">
                Sažetak rezervacije
              </h2>
              {selectedProduct ? <div className="mt-6 space-y-4" data-id="element-505" data-mp="2rbe4a2c191a8">
                  <div className="flex items-center gap-3" data-id="element-506" data-mp="2rdqvl63oknzy">
                    <div className="h-14 w-14 overflow-hidden rounded-xl bg-primary/10" data-id="element-507" data-mp="2w9pg2ilw1lv3">
                      {isImageUrl(selectedProduct.image) ? <img src={selectedProduct.image} alt="" className="h-full w-full object-cover" data-id="element-508" data-mp="2mkxsemnv1npz" /> : <span className="flex h-full items-center justify-center text-2xl" data-id="element-509" data-mp="3f1i6c59kosz4">
                          {selectedProduct.image}
                        </span>}
                    </div>
                    <strong data-id="element-510" data-mp="ymestffwdvhl">{selectedProduct.name}</strong>
                  </div>
                  <Summary label="Termin" value={startDate ? `${startDate}${endDate ? ` – ${endDate}` : ''}` : '-'} data-id="element-511" data-mp="1db19bkh9gtae" />
                  <Summary label="Vrijeme" value={`${startTime} – ${endTime}`} data-id="element-512" data-mp="fokeclw02rrt" />
                  <Summary label="Ukupno" value={`${total} €`} data-id="element-513" data-mp="umcsb9lwf83w" />
                </div> : <p className="mt-6 text-gray-500" data-id="element-514" data-mp="plp56z0sjbey">
                  Odaberite proizvod za prikaz sažetka.
                </p>}
            </div>
          </aside>
        </div>
      </div>
    </div>;
}
function BookingChannelButton({
  icon,
  label,
  description,
  className,
  onClick
}: {
  icon: React.ReactNode;
  label: string;
  description: string;
  className: string;
  onClick: () => void;
}) {
  return <button type="button" onClick={onClick} className={`flex w-full items-center gap-4 rounded-2xl border p-4 text-left transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${className}`} data-id="element-515" data-mp="2d618c1se5x0h">
      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/80 shadow-sm" data-id="element-516" data-mp="1pem2w05bizl0">
        {icon}
      </span>
      <span data-id="element-517" data-mp="1ue8giap26huc">
        <span className="block font-bold" data-id="element-518" data-mp="1udqawngiar9r">{label}</span>
        <span className="mt-1 block text-xs leading-5 text-gray-600" data-id="element-519" data-mp="1zc09l81fwa04">
          {description}
        </span>
      </span>
    </button>;
}
function Field({
  label,
  children
}: {
  label: string;
  children: React.ReactNode;
}) {
  return <label className="block text-sm font-bold text-gray-700" data-id="element-520" data-mp="16nm6wezeopi8">
      <span className="mb-2 block" data-id="element-521" data-mp="1bm5tmhltq5bn">{label}</span>
      {children}
    </label>;
}
function Summary({
  label,
  value
}: {
  label: string;
  value: string;
}) {
  return <div className="flex justify-between border-t border-gray-100 pt-3" data-id="element-522" data-mp="3kvng5w5igbwt">
      <span className="text-gray-500" data-id="element-523" data-mp="3kwexq329w943">{label}</span>
      <strong className="text-right text-gray-900" data-id="element-524" data-mp="3fw0z1bmkabiq">{value}</strong>
    </div>;
}
function rangeAvailable(productId: string, start: string, end: string, availability: AvailabilityData): boolean {
  let date = new Date(`${start}T00:00:00`);
  const last = new Date(`${end}T00:00:00`);
  while (date <= last) {
    const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    if (availability.bookings.some(booking => booking.productId === productId && value >= booking.date && value <= (booking.endDate || booking.date)) || availability.blockedDates.some(block => (!block.productId || block.productId === productId) && value >= block.date && value <= (block.endDate || block.date))) return false;
    date.setDate(date.getDate() + 1);
  }
  return true;
}
function createId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}
function normalizeCroatianPhone(phone: string): string {
  const digits = phone.replace(/\D/g, '');
  if (!digits) return '';
  if (digits.startsWith('00')) return digits.slice(2);
  return digits.startsWith('0') ? `385${digits.slice(1)}` : digits;
}
function buildViberChatUrl(phone: string): string {
  return `viber://chat/?number=${encodeURIComponent(`+${phone}`)}`;
}
function buildBookingMessage(booking: BookingType): string {
  return ['NOVA REZERVACIJA', '', `Klijent: ${booking.customerName}`, `Telefon: ${booking.phone}`, `Email: ${booking.email || '-'}`, `Adresa: ${booking.eventAddress}`, '', `Proizvod: ${booking.productName}`, booking.selectedVariant ? `Opcija: ${booking.selectedVariant}` : '', booking.endDate && booking.endDate !== booking.date ? `Termin: ${booking.date} – ${booking.endDate}` : `Datum: ${booking.date}`, `Vrijeme: ${booking.startTime} - ${booking.endTime}`, `Dostava: ${booking.includeDelivery ? 'Da' : 'Ne (preuzimanje)'}`, `Cijena: ${booking.totalPrice} €`, booking.note ? `Napomena: ${booking.note}` : '', booking.locationImage || booking.locationImagePath ? 'Klijent je priložio sliku lokacije (vidljiva u admin panelu)' : ''].filter(Boolean).join('\n');
}
function isImageUrl(value: string): boolean {
  return value.startsWith('http') || value.startsWith('data:');
}